import React, { type ReactNode } from "react";
import { Flex } from "src/components/Flex.tsx";
import { H6 } from "src/components/HeadingV2/HeadingV2.tsx";
import { Link } from "src/components/TextV2/Link.tsx";
import { AssignedRecordsTable } from "src/components/Form/panels/TransferPanel/views/AssignedRecordsTable/AssignedRecordsTable.tsx";
import { ButtonV2 } from "src/components/ButtonV2/ButtonV2.tsx";
import { ModalV2 } from "src/components/ModalV2/ModalV2.tsx";

import { useBoolean } from "src/hooks/use-boolean.ts";
import { TransferList } from "src/components/Form/panels/TransferPanel/TransferList.tsx";
import { useTranslation } from "react-i18next";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { ResourceEnum } from "src/constants/constants.ts";
import { useResourceFormContext } from "src/components/Form/hooks/useResourceFormContext.ts";
import { TooltipV2 } from "src/components/TooltipV2/TooltipV2.tsx";

export type PanelFaceProps = {
  resource: string;
  formResource: string;
  isExcluded: boolean;
  rows: { title: string; id: string }[];
  rowsCount: number;
};

const INLINE_ROWS_LIMIT = 3;

export const PanelFace = (props: PanelFaceProps) => {
  const { rows, resource, rowsCount, isExcluded, ...restProps } = props;

  const modal = useBoolean(false);

  const { t } = useTranslation(TranslationNamespaceEnum.TransferPanel);
  const formContext = useResourceFormContext();

  const titleKey = isExcluded
    ? rowsCount > 0
      ? "exclude.face.title.some"
      : "exclude.face.title.none"
    : rowsCount > 0
    ? "include.face.title.some"
    : "include.face.title.none";

  return (
    <Flex gap={10} flexDirection={"column"}>
      <Flex alignItems={"center"} gap={10}>
        <H6>{t(titleKey)}</H6>
        {rowsCount > INLINE_ROWS_LIMIT && (
          <Link onClick={modal.setTrue} variant={"body1"}>
            ({rowsCount})
          </Link>
        )}
      </Flex>
      {rowsCount <= INLINE_ROWS_LIMIT && <AssignedRecordsTable rows={rows} />}
      <div>
        <TooltipV2
          disabled={formContext.action === "edit"}
          content={t("disabledFunctionality", {
            ns: TranslationNamespaceEnum.FormComponent,
          })}
        >
          <ButtonV2
            onClick={modal.setTrue}
            disabled={formContext.action === "create"}
            startAdornment={RESOURCE_ICON_MAP[resource]}
            size={"sm"}
          >
            {isExcluded
              ? t("exclude.face.button.text")
              : t("include.face.button.text")}
          </ButtonV2>
        </TooltipV2>

        {modal.value && (
          <ModalV2 onClose={modal.setFalse}>
            <TransferList
              resource={resource}
              onClose={modal.setFalse}
              isExcluded={isExcluded}
              {...restProps}
            />
          </ModalV2>
        )}
      </div>
    </Flex>
  );
};

const RESOURCE_ICON_MAP: Record<string, ReactNode> = {
  [ResourceEnum.Customer]: <span className="icon-15-customers" />,
  [ResourceEnum.CustomerGroup]: <span className="icon-15-customers" />,
  [ResourceEnum.Country]: <span className="icon-15-countries" />,
  [ResourceEnum.Variant]: <span className="icon-15-slct_gds" />,
  [ResourceEnum.Category]: <span className="icon-15-categories" />,
  [ResourceEnum.ProductGroup]: <span className="icon-15-categories" />,
  [ResourceEnum.DeliveryService]: <span className="icon-15-deliv_cost" />,
};
